/**
 * Taking part in a campaign (specification §6.3), as numbered steps: sign in, link accounts on
 * the campaign's platform, publish content that follows the brief, and submit it while the
 * campaign runs, from any of the linked accounts. Each account has its own slots, and a wallet
 * takes part with at most the campaign's number of accounts (§6.4). Before the start, creators
 * can take the first two steps. After the end, they still see their submissions. The API runs
 * the intake checks and the page explains each outcome, and later each check Morcast runs. A
 * YouTube submission uploads the video's source file first, and an INTEGRATION submission
 * declares the segment with the promotion. Once no linked account can submit any more, the form
 * gives way to what the creator can still do. Withdrawing, which keeps the slot used, is
 * confirmed first.
 */

import { Check, FileVideo } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router";

import {
  type LinkedAccount,
  PLATFORM_DETAILS,
  shownHandle,
  useLinkedAccounts,
} from "../../accounts/accounts.js";
import { useSession } from "../../auth/session.js";
import type { PublicCampaign } from "../../campaigns/campaigns.js";
import {
  checkProblems,
  formatVideoTime,
  inReview,
  parseVideoTime,
  readVideoDuration,
  rejectionText,
  type Submission,
  type MySubmissions as SubmissionsWithSlots,
  useMySubmissions,
  useSubmit,
  useSubmitVideo,
  useWithdraw,
} from "../../campaigns/submissions.js";
import { ConfirmDialog } from "../../components/ConfirmDialog.js";
import { FileInput } from "../../components/forms.js";
import { SignInDialog } from "../../components/SignInDialog.js";
import { Badge, ErrorMessage, styles } from "../../components/ui.js";
import { formatUtc } from "../../format.js";

/**
 * Whether the wallet's linked accounts can still submit here (§6.4): "open" when one of them has
 * a slot left. "link" when none has, but another account could still take part. "full" when the
 * wallet takes part with the most accounts the campaign allows and each used all its slots.
 */
export function submissionRoom(
  linked: Pick<LinkedAccount, "accountId">[],
  slots: SubmissionsWithSlots["slots"],
): "open" | "link" | "full" {
  const used = new Map(slots.accounts.map((entry) => [entry.accountId, entry.used]));
  const canTakeMore = used.size < slots.accountLimit;
  const open = linked.some((account) => {
    const count = used.get(account.accountId);
    return count === undefined ? canTakeMore : count < slots.limit;
  });
  return open ? "open" : canTakeMore ? "link" : "full";
}

export function TakePart({ campaign }: { campaign: PublicCampaign }) {
  const session = useSession();
  const signedIn = Boolean(session.data);
  const accounts = useLinkedAccounts(signedIn);
  const linked = accounts.data?.filter((entry) => entry.platform === campaign.platform) ?? [];
  const account = linked.length > 0;
  const open = campaign.phase === "running";
  const youtube = campaign.platform === "YOUTUBE";
  const accountName = PLATFORM_DETAILS[campaign.platform].account;
  const mine = useMySubmissions(campaign, signedIn && campaign.phase !== "upcoming");
  const room = mine.data ? submissionRoom(linked, mine.data.slots) : "open";

  return (
    <section aria-labelledby="take-part-title" className={`mt-6 text-sm ${styles.card}`}>
      <h2
        id="take-part-title"
        className="font-display text-lg font-semibold tracking-[-0.015em] text-zinc-950 dark:text-white"
      >
        Take part
      </h2>
      {campaign.phase === "upcoming" && (
        <p className="mt-2 text-zinc-700 dark:text-zinc-300">
          Submissions open when the campaign starts, {formatUtc(campaign.startAt)}. Get ready now by
          signing in and linking your {accountName}.
        </p>
      )}
      {!open && campaign.phase !== "upcoming" && (
        <p className="mt-2 text-zinc-700 dark:text-zinc-300">Submissions are closed.</p>
      )}

      {(open || campaign.phase === "upcoming") && session.isSuccess && (
        <ol className="mt-5 space-y-5">
          <Step number={1} done={signedIn} title="Sign in with your wallet">
            {!signedIn && <SignInPrompt />}
          </Step>
          <Step number={2} done={account} title={`Link your ${accountName}`}>
            {account ? (
              <p className="text-zinc-500 dark:text-zinc-400">
                Linked: {linked.map((entry) => shownHandle(entry)).join(", ")}.{" "}
                <Link to="/accounts" className={styles.link}>
                  Link another
                </Link>
              </p>
            ) : (
              signedIn &&
              accounts.isSuccess && (
                <p className="text-zinc-700 dark:text-zinc-300">
                  <Link to="/accounts" className={styles.link}>
                    {youtube ? "Link your YouTube channel" : "Link your X account"}
                  </Link>{" "}
                  to submit your {youtube ? "videos" : "posts"}.
                </p>
              )
            )}
          </Step>
          <Step
            number={3}
            title={
              youtube
                ? "Publish a video that follows the brief"
                : "Publish a post that follows the brief"
            }
          >
            <p className="text-zinc-600 dark:text-zinc-400">
              Show the disclosure "{campaign.brief.disclosure}" and include everything the brief
              asks for. Only organic reach counts, so never promote it with ads.
            </p>
          </Step>
          <Step number={4} title={youtube ? "Submit the video" : "Submit the post's address"}>
            <p className="mb-2 text-zinc-600 dark:text-zinc-400">
              Submit from any of your linked {accountName}s. Each may submit{" "}
              {campaign.submissionLimit}, and you can take part with up to {campaign.accountLimit}.
            </p>
            {open && signedIn && account && room !== "open" ? (
              <p className="text-zinc-700 dark:text-zinc-300">
                {room === "link" ? (
                  <>
                    Your linked {accountName}s have used all their submissions here.{" "}
                    <Link to="/accounts" className={styles.link}>
                      Link another {accountName}
                    </Link>{" "}
                    to submit more.
                  </>
                ) : (
                  `You take part with ${campaign.accountLimit} ${accountName}s, the most this campaign allows, and each has used its ${campaign.submissionLimit} submissions.`
                )}
              </p>
            ) : open && signedIn && account ? (
              youtube ? (
                <VideoForm campaign={campaign} submissions={mine.data?.submissions ?? []} />
              ) : (
                <SubmitForm campaign={campaign} submissions={mine.data?.submissions ?? []} />
              )
            ) : (
              <p className="text-zinc-600 dark:text-zinc-400">
                {open
                  ? "Once the steps above are done, submit it here while the campaign runs."
                  : "You can submit once the campaign starts, until its end."}
              </p>
            )}
          </Step>
        </ol>
      )}

      {signedIn && campaign.phase !== "upcoming" && (
        <MySubmissions campaign={campaign} linked={accounts.data ?? []} />
      )}
    </section>
  );
}

/** A numbered step, checked once done, with what the creator does there. */
function Step({
  number,
  done = false,
  title,
  children,
}: {
  number: number;
  done?: boolean;
  title: string;
  children?: ReactNode;
}) {
  return (
    <li className="flex gap-3">
      <span
        aria-hidden
        className={`mt-0.5 grid size-6 shrink-0 place-items-center rounded-full text-xs font-semibold ${
          done
            ? "bg-gradient-to-br from-brand-400 to-brand-600 text-white shadow-[0_4px_12px_-4px_rgb(65_168_198/0.6)]"
            : "bg-zinc-900/[0.04] text-zinc-600 ring-1 ring-zinc-900/10 ring-inset dark:bg-white/[0.05] dark:text-zinc-300 dark:ring-white/15"
        }`}
      >
        {done ? <Check className="size-3.5" /> : number}
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-zinc-900 dark:text-zinc-100">
          {title}
          {done && <span className="sr-only"> (done)</span>}
        </p>
        {children && <div className="mt-1">{children}</div>}
      </div>
    </li>
  );
}

function SignInPrompt() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <p className="text-zinc-700 dark:text-zinc-300">
        Sign in with the wallet you want to be paid in to submit your content.
      </p>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`mt-2 ${styles.secondaryButton}`}
      >
        Sign in
      </button>
      <SignInDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/** The wallet's submissions to the campaign, and the slots each of its accounts used. */
function MySubmissions({
  campaign,
  linked,
}: {
  campaign: PublicCampaign;
  linked: LinkedAccount[];
}) {
  const mine = useMySubmissions(campaign, true);
  if (mine.isError) {
    return <ErrorMessage error={mine.error} />;
  }
  if (!mine.data || mine.data.submissions.length === 0) {
    return null;
  }
  const { slots } = mine.data;
  /** An account as the creator knows it, or its ID once it is no longer linked. */
  const nameOf = (accountId: string) => {
    const account = linked.find((entry) => entry.accountId === accountId);
    return account ? shownHandle(account) : accountId;
  };
  /** The submissions an account has left here. */
  const leftOf = (accountId: string) =>
    slots.limit - (slots.accounts.find((entry) => entry.accountId === accountId)?.used ?? 0);
  return (
    <div className="mt-6 border-t border-zinc-900/[0.07] pt-5 dark:border-white/10">
      <h3 className="font-display font-semibold text-zinc-950 dark:text-white">
        Your submissions{" "}
        <span className="font-normal text-zinc-500 dark:text-zinc-400">
          ({slots.accounts.length} of {slots.accountLimit} accounts)
        </span>
      </h3>
      {slots.accounts.length > 0 && (
        <ul aria-label="Slots used" className="mt-2 flex flex-wrap gap-2">
          {slots.accounts.map((entry) => (
            <li
              key={entry.accountId}
              className="rounded-full bg-zinc-900/[0.04] px-3 py-1 text-xs text-zinc-700 ring-1 ring-zinc-900/[0.06] ring-inset dark:bg-white/[0.05] dark:text-zinc-300 dark:ring-white/10"
            >
              <span className="font-medium">{nameOf(entry.accountId)}</span> {entry.used} of{" "}
              {slots.limit}
            </li>
          ))}
        </ul>
      )}
      <ul className="mt-3 divide-y divide-zinc-900/[0.07] dark:divide-white/10">
        {mine.data.submissions.map((submission) => (
          <SubmissionRow
            key={submission.id}
            campaign={campaign}
            submission={submission}
            account={submission.accountId ? nameOf(submission.accountId) : null}
            left={submission.accountId ? leftOf(submission.accountId) : null}
          />
        ))}
      </ul>
    </div>
  );
}

/**
 * The latest state of the form's last submission: from the wallet's list, which follows a
 * withdrawal, or as the API answered it.
 */
function latest(outcome: Submission | undefined, submissions: Submission[]) {
  return outcome && (submissions.find((entry) => entry.id === outcome.id) ?? outcome);
}

function SubmitForm({
  campaign,
  submissions,
}: {
  campaign: PublicCampaign;
  submissions: Submission[];
}) {
  const submit = useSubmit(campaign);
  const [url, setUrl] = useState("");

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    // A refused address stays, so that the creator can correct it.
    submit.mutate(url.trim(), {
      onSuccess: (recorded) => recorded.status === "accepted" && setUrl(""),
    });
  }

  return (
    <form onSubmit={onSubmit} className="mt-3">
      <label className="block font-medium">
        Address of your post
        <input
          type="url"
          required
          value={url}
          onChange={(event) => {
            setUrl(event.target.value);
            // The last outcome was about the address as it was.
            submit.reset();
          }}
          placeholder="https://x.com/you/status/…"
          className={styles.input}
        />
      </label>
      <button type="submit" disabled={submit.isPending} className={`mt-3 ${styles.primaryButton}`}>
        {submit.isPending ? "Checking your post…" : "Submit"}
      </button>
      <Outcome outcome={latest(submit.data, submissions)} platform="X" />
      {submit.isError && <ErrorMessage error={submit.error} />}
    </form>
  );
}

/**
 * A YouTube video, with its source file and, for INTEGRATION, the segment with the promotion. An
 * accepted video empties the form. After any other outcome it keeps what the creator entered, and
 * the next try reuses the upload (see `useSubmitVideo`).
 */
function VideoForm({
  campaign,
  submissions,
}: {
  campaign: PublicCampaign;
  submissions: Submission[];
}) {
  const submit = useSubmitVideo(campaign);
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const fileLabelId = useId();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const integration = campaign.format === "INTEGRATION";
  const segment = { start: parseVideoTime(start), end: parseVideoTime(end) };
  const segmentOrdered =
    segment.start !== null && segment.end !== null && segment.start < segment.end;
  // The chosen file's length, when the browser can read it, to catch a segment past its end.
  const [duration, setDuration] = useState<number | null>(null);
  const chosen = useRef<File | null>(null);
  useEffect(() => {
    chosen.current = file;
    setDuration(null);
    if (file && integration) {
      void readVideoDuration(file).then((seconds) => {
        if (chosen.current === file) {
          setDuration(seconds);
        }
      });
    }
  }, [file, integration]);
  const pastEnd = duration !== null && segment.end !== null && segment.end > duration;
  const segmentReady = !integration || (segmentOrdered && !pastEnd);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!file || !segmentReady) {
      return;
    }
    submit.mutate(
      {
        url: url.trim(),
        file,
        ...(integration ? { segment: { start: segment.start ?? 0, end: segment.end ?? 0 } } : {}),
      },
      {
        onSuccess: (recorded) => {
          if (recorded.status === "accepted") {
            setUrl("");
            setFile(null);
            setStart("");
            setEnd("");
          }
        },
      },
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-3 space-y-3">
      <label className="block font-medium">
        Address of your video
        <input
          type="url"
          required
          value={url}
          onChange={(event) => {
            setUrl(event.target.value);
            submit.reset();
          }}
          placeholder="https://www.youtube.com/watch?v=…"
          className={styles.input}
        />
      </label>
      <div>
        <span id={fileLabelId} className="block font-medium">
          The video file you published
        </span>
        <FileInput
          labelId={fileLabelId}
          accept="video/*"
          file={file}
          onChange={setFile}
          prompt="Choose the video file"
          types="MP4, MOV, WebM or another video"
          icon={FileVideo}
        />
      </div>
      <p className="text-xs text-zinc-500 dark:text-zinc-400">
        Morcast checks your content on this file, so it must be the video you published. Morcast
        never downloads videos from YouTube.
      </p>
      {integration && (
        <fieldset>
          <legend className="font-medium">The segment with the promotion</legend>
          <div className="mt-1 flex gap-3">
            <label className="text-sm">
              Starts at
              <input
                required
                value={start}
                onChange={(event) => setStart(event.target.value)}
                placeholder="1:30"
                className={styles.input}
              />
            </label>
            <label className="text-sm">
              Ends at
              <input
                required
                value={end}
                onChange={(event) => setEnd(event.target.value)}
                placeholder="2:45"
                className={styles.input}
              />
            </label>
          </div>
          {start !== "" && end !== "" && !segmentOrdered && (
            <p className="mt-1 text-xs text-red-700 dark:text-red-400">
              Write times as minutes and seconds, such as 1:30, the end after the start.
            </p>
          )}
          {segmentOrdered && pastEnd && duration !== null && (
            <p className="mt-1 text-xs text-red-700 dark:text-red-400">
              The segment must end within the video, which lasts {formatVideoTime(duration)}.
            </p>
          )}
        </fieldset>
      )}
      <button
        type="submit"
        disabled={submit.isPending || !file || !segmentReady}
        className={styles.primaryButton}
      >
        {submit.progress !== null
          ? `Uploading… ${Math.round(submit.progress * 100)}%`
          : submit.isPending
            ? "Checking your video…"
            : "Submit"}
      </button>
      <Outcome outcome={latest(submit.data, submissions)} platform="YOUTUBE" />
      {submit.isError && <ErrorMessage error={submit.error} />}
    </form>
  );
}

/** What became of the last submission, unless it was withdrawn since. */
function Outcome({
  outcome,
  platform,
}: {
  outcome: Submission | undefined;
  platform: "X" | "YOUTUBE";
}) {
  const content = platform === "X" ? "post" : "video";
  return (
    <div aria-live="polite" className="mt-3 min-h-5">
      {outcome?.status === "accepted" && checkProblems(outcome).length === 0 && (
        <p className="text-emerald-700 dark:text-emerald-400">
          Accepted. Your {content} takes part in the campaign.
        </p>
      )}
      {outcome?.status === "accepted" && checkProblems(outcome).length > 0 && (
        <p role="alert" className="text-amber-700 dark:text-amber-300/90">
          Accepted, but your {content} will fail verification. {checkProblems(outcome).join(" ")}
        </p>
      )}
      {outcome?.status === "rejected" && (
        <p role="alert" className="text-red-700 dark:text-red-400">
          Not accepted. {rejectionText(outcome.reason, platform)}
        </p>
      )}
    </div>
  );
}

function SubmissionRow({
  campaign,
  submission,
  account,
  left,
}: {
  campaign: PublicCampaign;
  submission: Submission;
  /** The account that published it, as the creator knows it. */
  account: string | null;
  /** The submissions that account has left here. */
  left: number | null;
}) {
  const withdraw = useWithdraw();
  const [confirming, setConfirming] = useState(false);
  const problems = submission.status === "accepted" ? checkProblems(submission) : [];
  const reviewing = submission.status === "accepted" && inReview(submission);
  const running = campaign.phase === "running";
  const content = campaign.platform === "X" ? "post" : "video";
  return (
    <li className="py-3">
      <a
        href={submission.url}
        className="block font-medium break-all text-zinc-900 hover:text-brand-700 hover:underline dark:text-zinc-100 dark:hover:text-brand-300"
      >
        {submission.url}
      </a>
      <div className="mt-1.5 flex items-center justify-between gap-3">
        <p className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-zinc-500 dark:text-zinc-400">
          {submission.status === "accepted" && <Badge tone="green">Accepted</Badge>}
          {submission.status === "withdrawn" && <Badge tone="grey">Withdrawn</Badge>}
          {submission.status === "rejected" && <Badge tone="red">Not accepted</Badge>}
          <span>
            {account && `${account} · `}
            {formatUtc(submission.receivedAt)}
          </span>
        </p>
        {submission.status === "accepted" && running && (
          <button
            type="button"
            disabled={withdraw.isPending}
            onClick={() => setConfirming(true)}
            className={`shrink-0 ${styles.secondaryButtonSmall}`}
          >
            {withdraw.isPending ? "Withdrawing…" : "Withdraw"}
          </button>
        )}
      </div>
      <ConfirmDialog
        open={confirming}
        title={`Withdraw this ${content}?`}
        confirmLabel={`Withdraw ${content}`}
        cancelLabel="Keep it"
        onConfirm={() => withdraw.mutate(submission.id)}
        onClose={() => setConfirming(false)}
      >
        <p>
          It no longer takes part and earns nothing. Its slot stays used, and submitting it again
          takes another
          {left === null
            ? "."
            : left === 0
              ? `, but ${account ?? "its account"} has none left here.`
              : `. ${account ?? "Its account"} has ${left} left here.`}
        </p>
      </ConfirmDialog>
      {submission.status === "rejected" && (
        <p className="mt-2 text-zinc-600 dark:text-zinc-400">
          {rejectionText(submission.reason, campaign.platform)}
        </p>
      )}
      {problems.length > 0 && (
        <p className="mt-2 text-amber-700 dark:text-amber-300/90">
          This {content} will fail verification. {problems.join(" ")}
          {running &&
            ` Withdraw it and submit a ${content} that meets the brief. A withdrawn ${content} still uses its slot.`}
        </p>
      )}
      {reviewing && problems.length === 0 && (
        <p className="mt-2 text-zinc-600 dark:text-zinc-400">
          A person at Morcast is checking part of this {content}. The result will say what they
          decided.
        </p>
      )}
      {withdraw.isError && <ErrorMessage error={withdraw.error} />}
    </li>
  );
}
